import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Button,
  Avatar, Stack, Alert, CircularProgress, Tooltip, Switch,
  FormControlLabel, TextField, InputAdornment, Paper
} from '@mui/material';
import {
  FamilyRestroom, Search, Delete, OpenInNew,
  CheckCircle, Cancel, PersonOff
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { parentAdminAPI } from '../services/api';

export default function ParentManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  const load = () => {
    setLoading(true);
    parentAdminAPI.listParents()
      .then(r => { if (r.data.success) setParents(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load parent accounts', { variant: 'error' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (id, current) => {
    try {
      await parentAdminAPI.toggleActive(id, !current);
      setParents(prev => prev.map(p => p.id === id ? { ...p, is_active: !current ? 1 : 0 } : p));
      enqueueSnackbar(`Account ${!current ? 'activated' : 'deactivated'}`, { variant: 'success' });
    } catch (err) { enqueueSnackbar(err.message, { variant: 'error' }); }
  };

  const deleteParent = async (id, name) => {
    if (!window.confirm(`Delete parent account for ${name}? This cannot be undone.`)) return;
    try {
      await parentAdminAPI.deleteParent(id);
      setParents(prev => prev.filter(p => p.id !== id));
      enqueueSnackbar('Parent account deleted', { variant: 'success' });
    } catch (err) { enqueueSnackbar(err.message, { variant: 'error' }); }
  };

  const filtered = parents.filter(p =>
    p.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) ||
    p.children?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount   = parents.filter(p => p.is_active).length;
  const inactiveCount = parents.filter(p => !p.is_active).length;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FamilyRestroom color="primary" /> Parent Portal Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage parent accounts — activate, deactivate or remove access
        </Typography>
      </Box>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <Paper variant="outlined" sx={{ p: 2, minWidth: 130, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={700} color="primary">{parents.length}</Typography>
          <Typography variant="caption" color="text.secondary">Total Accounts</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 130, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={700} color="success.main">{activeCount}</Typography>
          <Typography variant="caption" color="text.secondary">Active</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 130, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={700} color="error.main">{inactiveCount}</Typography>
          <Typography variant="caption" color="text.secondary">Deactivated</Typography>
        </Paper>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<OpenInNew />}
          onClick={() => window.open('/parent', '_blank')}>
          Open Parent Portal
        </Button>
      </Stack>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Search by name, phone or child…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ width: 300 }} />
      </Box>

      {loading ? <CircularProgress /> : (
        parents.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <FamilyRestroom sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>No parent accounts registered yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Parents can self-register at the Parent Portal link below
            </Typography>
            <Button sx={{ mt: 2 }} variant="outlined" startIcon={<OpenInNew />}
              onClick={() => window.open('/parent', '_blank')}>
              Open Parent Portal
            </Button>
          </Paper>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Parent</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Phone</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Children Linked</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Last Login</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((p, i) => (
                    <TableRow key={p.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 30, height: 30, fontSize: 12,
                            bgcolor: p.is_active ? 'primary.main' : 'grey.400' }}>
                            {p.full_name?.[0]}
                          </Avatar>
                          <Typography fontSize={13} fontWeight={500}>{p.full_name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{p.phone}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {p.children_count > 0 ? (
                            <Chip label={`${p.children_count} child${p.children_count > 1 ? 'ren' : ''}: ${p.children}`}
                              size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                          ) : (
                            <Chip label="No children linked" size="small" sx={{ fontSize: 11 }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {p.last_login
                          ? new Date(p.last_login).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : 'Never'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={p.is_active ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                          label={p.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={p.is_active ? 'success' : 'default'}
                          sx={{ fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" justifyContent="center" spacing={0.5}>
                          <Tooltip title={p.is_active ? 'Deactivate account' : 'Activate account'}>
                            <IconButton size="small"
                              color={p.is_active ? 'warning' : 'success'}
                              onClick={() => toggleActive(p.id, !!p.is_active)}>
                              {p.is_active ? <PersonOff fontSize="small" /> : <CheckCircle fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete account permanently">
                            <IconButton size="small" color="error"
                              onClick={() => deleteParent(p.id, p.full_name)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )
      )}

      <Alert severity="info" sx={{ mt: 3, fontSize: 12 }}>
        <strong>Parent Portal URL:</strong> Share <code>{window.location.origin}/parent</code> with parents to allow self-registration.
        Parents must provide their child's admission number, class, gender, date of birth, and the phone number registered with the school.
      </Alert>
    </Box>
  );
}
